<template>
  <button class="btn-cmp" :class="[props.variant, props.size, { disabled: props.disabled, loading: props.isLoading, 'no-hover': disableHover, mobile: isMobileLayout, 'full-size': props.isFullSize }]" @click.stop="(e) => emit('click', e)">
    <div v-if="props.isLoading" class="spinner-container">
      <LoadingSpinner class="spinner" :size="'mini'" :is-mini-white="props.variant === ButtonVariant.PRIMARY" />
    </div>
    <div>
      <slot>{{ props.text }}</slot>
    </div>
  </button>
</template>

<script setup lang="ts">
import { useViewPort } from '@/composables/viewPort'
import { BaseButton, ButtonSize, ButtonVariant } from '@/styles/types'
import LoadingSpinner from '../LoadingSpinner.vue'

const { isMobileLayout } = useViewPort()

const props = withDefaults(defineProps<BaseButton>(), {
  variant: ButtonVariant.PRIMARY,
  size: ButtonSize.BIG,
  disabled: false,
  isFullSize: false,
  isLoading: false
})

const emit = defineEmits(['click'])
</script>

<style scoped lang="scss">
@import '@/styles/_variables.scss';

.btn-cmp {
  font-family: 'Nunito', sans-serif;
  color: $white;
  background-color: $white;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.2px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  border-radius: 4px;
  width: fit-content;
  height: 36px;
  padding: 8px 12px;
  margin: 0;
  cursor: pointer;

  &.loading {
    pointer-events: none;
  }

  .spinner-container {
    margin-inline-end: 0.5rem;
  }

  &.full-size {
    width: 100% !important;
  }

  &.primary {
    background-color: $rebranding-primary-500;

    &.disabled {
      background-color: $rebranding-grey-500;
    }

    &:not(.no-hover):hover {
      background-color: $rebranding-primary-600;
    }
  }

  &.secondary {
    background-color: $rebranding-primary-50;
    color: $rebranding-primary-500;

    &.disabled {
      color: $rebranding-grey-500;
      background-color: $rebranding-grey-300;
      opacity: 1;
    }

    &:not(.no-hover):hover {
      background-color: $rebranding-primary-100;
    }
  }

  // 3th button
  &.tertiary {
    color: $rebranding-primary-500;
    background-color: transparent;

    &.disabled {
      color: $rebranding-grey-500;
    }

    &:not(.no-hover):hover {
      color: $rebranding-primary-600;
      background-color: $rebranding-primary-50;
    }
  }

  &.tertiary-gray {
    color: $secondaryTitleText;
    background-color: transparent;

    &.disabled {
      color: $disabledText;
      opacity: 1;
    }

    &:not(.no-hover):hover {
      background-color: $display-neutrals-100;
    }
  }

  &.link {
    @extend .tertiary;
    text-decoration: underline;
  }

  &.medium {
    font-size: 12px;
    height: 30px;
    padding: 4px 10px;
  }

  &.small {
    font-size: 12px;
    height: 24px;
    padding: 2px 6px;
  }

  &.mobile {
    font-size: 16px;
  }
}
</style>
